import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Eye, EyeOff, Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { revealLoginPassword } from '@/features/onlineAccounts/hooks';
import { generatePassword } from '@/features/onlineAccounts/model';
import { cn } from '@/lib/utils';

/** How long a revealed password stays on screen. */
const REVEAL_SECONDS = 30;

const iconButton =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-50';

/**
 * Copies text produced by an async call. Safari only allows clipboard
 * writes inside the click, so the pending value goes in a ClipboardItem
 * there; elsewhere it falls back to writeText once the value arrives.
 */
async function copyAsync(getText: () => Promise<string>): Promise<void> {
  const pending = getText();
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': pending.then((text) => new Blob([text], { type: 'text/plain' })),
        }),
      ]);
      return;
    } catch {
      // Fall through: Firefox and older Chrome reject promise-valued items.
    }
  }
  await navigator.clipboard.writeText(await pending);
}

/**
 * A saved password: hidden until asked for, then shown for
 * REVEAL_SECONDS. Every reveal and copy is fetched fresh from the
 * online-account-secrets function, which writes it to the audit log.
 * Give it a `key` that changes with the password so a stale value is
 * never left on screen.
 */
export function SavedPassword({
  loginId,
  hasPassword,
  className,
}: {
  loginId: string;
  hasPassword: boolean;
  className?: string;
}) {
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState<'view' | 'copy' | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (value === null) return;
    const timer = window.setTimeout(() => {
      if (secondsLeft <= 1) setValue(null);
      else setSecondsLeft(secondsLeft - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [value, secondsLeft]);

  if (!hasPassword) {
    return <span className={cn('text-sm text-muted-foreground', className)}>Not saved</span>;
  }

  const hide = () => setValue(null);

  const reveal = async () => {
    setBusy('view');
    try {
      const password = await revealLoginPassword(loginId, 'view');
      setValue(password);
      setSecondsLeft(REVEAL_SECONDS);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not show the password.');
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    setBusy('copy');
    try {
      await copyAsync(() => revealLoginPassword(loginId, 'copy'));
      toast.success('Password copied.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not copy the password.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-0.5', className)}>
      <span
        className={cn(
          'min-w-0 truncate font-mono text-sm',
          value ? 'select-all text-foreground' : 'tracking-widest text-muted-foreground',
        )}
        title={value ? `Hides in ${secondsLeft}s` : undefined}
      >
        {value ?? '••••••••••'}
      </span>
      <button
        type="button"
        className={iconButton}
        aria-label={value ? 'Hide password' : 'Show password'}
        title={value ? `Hide (hides itself in ${secondsLeft}s)` : 'Show password'}
        disabled={busy !== null}
        onClick={(event) => {
          event.stopPropagation();
          if (value) hide();
          else void reveal();
        }}
      >
        {busy === 'view' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : value ? (
          <EyeOff className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </button>
      <button
        type="button"
        className={iconButton}
        aria-label="Copy password"
        title="Copy password"
        disabled={busy !== null}
        onClick={(event) => {
          event.stopPropagation();
          void copy();
        }}
      >
        {busy === 'copy' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
      </button>
    </span>
  );
}

/** Password entry with show/hide and a generator. Kept out of browser password managers. */
export function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="new-password"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          className="pr-11 font-mono"
        />
        <button
          type="button"
          className={cn(iconButton, 'absolute right-0.5 top-1/2 -translate-y-1/2')}
          aria-label={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <Button
        type="button"
        variant="outline"
        className="shrink-0"
        disabled={disabled}
        onClick={() => {
          onChange(generatePassword());
          setVisible(true);
        }}
      >
        <Wand2 className="h-4 w-4" />
        Generate
      </Button>
    </div>
  );
}
